import { empresaSchema } from '../../services/validators';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../components/globales/Loading';
import TablaLayout from '../../components/globales/TablaLayout';
import ModalDynamic from '../../components/globales/ModalDynamic';
import useCrud from '../../hooks/useCrud';
import ENDPOINTS from '../../api/endpoints';
import axios from '../../axiosConfig';

export default function Empresas() {
    const navigate = useNavigate();
    const keyField = 'codigo_empresa';
    const [hosts, setHosts] = useState([]);

    const empresasEndpoints = useMemo(() => ({
        listar: ENDPOINTS.empresas.listar,
        crear: ENDPOINTS.empresas.crear,
        consultar: ENDPOINTS.empresas.consultar,
        actualizar: ENDPOINTS.empresas.actualizar,
        borrar: ENDPOINTS.empresas.borrar
    }), []);

    // Columnas para crear (sin tenant)
    const createColumns = [
        { label: 'No', key: 'index', align: 'center', render: (_, i) => i + 1 },
        { label: 'Nit', key: 'nit', width: '150px' },
        { label: 'Nombre', key: 'nombre_empresa', width: '250px' },
        { label: 'Celular', key: 'celular', width: '150px' },
        { label: 'Dirección', key: 'direccion', width: '300px' },
        { label: 'Host', key: 'host_empresa', width: '200px' }
    ];

    // Columnas para editar/consultar (con tenant disabled)
    const editColumns = [
        { label: 'No', key: 'index', align: 'center', render: (_, i) => i + 1 },
        { label: 'Nit', key: 'nit', width: '150px' },
        { label: 'Nombre', key: 'nombre_empresa', width: '250px' },
        { label: 'Celular', key: 'celular', width: '150px' },
        { label: 'Dirección', key: 'direccion', width: '300px' },
        { label: 'Host', key: 'host_empresa', width: '200px' },
        { label: 'Tenant Empresa', key: 'tenant', width: '300px', disabled: true, readonly: true }
    ];

    const baseColumns = editColumns; 

    const tableColumns = [
        { label: 'No', key: 'index', align: 'center', render: (_, i) => i + 1 },
        { label: 'Nit', key: 'nit', width: '20%' },
        { label: 'Nombre', key: 'nombre_empresa', width: '40%' },
        { label: 'Tenant Empresa', key: 'tenant', width: '30%' }
    ];

    const initForm = () => ({
        nit: '',
        nombre_empresa: '',
        celular: '',
        direccion: '',
        host_empresa: '',
        tenant: ''
    });

    const {
        data,
        loading,
        error,
        showModal,
        actionType,
        formData,
        setFormData,
        errors,
        loadingModal,
        openModal: originalOpenModal,
        closeModal,
        handleChange,
        submit,
        remove,
        consultar
    } = useCrud(empresasEndpoints, { keyField, initForm, columns: baseColumns, schema: empresaSchema });

    // Carga de hosts al iniciar
    useEffect(() => {
        axios.get(ENDPOINTS.hosts.listarActivos)
            .then(res => {
                const raw = Array.isArray(res.data)
                    ? res.data
                    : Array.isArray(res.data.data)
                        ? res.data.data
                        : [];
                const mappedHosts = raw.map(h => ({
                    value: h.codigo,
                    label: h.descripcion
                }));
                setHosts(mappedHosts);
            })
            .catch(() => {});
    }, []);

    // Función para obtener las columnas correctas según el tipo de acción
    const getModalColumns = (actionType) => {
        const columnsToUse = actionType === 'crear' ? createColumns : editColumns;
        
        return columnsToUse.map(col => {
            if (col.key === 'host_empresa') {
                return { 
                    ...col, 
                    options: hosts,
                    type: 'select'
                };
            }
            if (col.key === 'tenant') {
                return { 
                    ...col, 
                    disabled: true, 
                    type: 'text'
                };
            }
            return col;
        });
    };

    // openModal optimizado: abre el modal inmediatamente y luego carga datos
    const openModal = useCallback((type, item = null) => {
        closeModal(); // Limpiar errores
        if (type === 'crear') {
            originalOpenModal('crear');
        } else if (type === 'editar' && item) {
            // Datos preliminares para renderizar sin delay
            const initialData = { ...item };
            // Convertir host_empresa a string para el select
            if (initialData.host_empresa !== undefined) {
                initialData.host_empresa = String(initialData.host_empresa);
            }
            // Asegurar que tenant tenga un valor por defecto
            if (!initialData.tenant) {
                initialData.tenant = '';
            }
            setFormData(initialData);
            originalOpenModal('editar', initialData);

            // Carga completa de datos en segundo plano
            if (empresasEndpoints.consultar) {
                consultar(item[keyField])
                    .then(datosCompletos => {
                        const finalData = datosCompletos || initialData;
                        if (finalData.host_empresa !== undefined) {
                            finalData.host_empresa = String(finalData.host_empresa);
                        }
                        // Asegurar que tenant tenga un valor
                        if (!finalData.tenant) {
                            finalData.tenant = '';
                        }
                        setFormData(finalData);
                    })
                    .catch(() => {});
            }
        } else {
            // Vista de consulta
            originalOpenModal('consultar', item);
        }
    }, [ originalOpenModal, consultar, keyField, empresasEndpoints.consultar, setFormData, closeModal ]);

    if (loading) return <Loading mensaje="Cargando Empresas..." />;
    if (error)   return <div className="text-danger">Error: {error.message}</div>;

    return (
        <div className="container">
            <TablaLayout
                title="Empresas"
                data={data}
                columns={tableColumns}
                keyField={keyField}
                onCreate={() => openModal('crear')}
                onView={item => openModal('consultar', item)}
                onEdit={item => openModal('editar', item)}
                onDelete={remove}
                onNavigate={item => navigate(`/empresas-codigo/${item[keyField]}`)}
                navigateTitle="Agregar Código"
                navigateIcon="note_add"
            />

            <ModalDynamic
                show={showModal}
                accion={actionType}
                title="Empresa"
                columns={getModalColumns(actionType)}
                formData={formData}
                errors={errors}
                loading={loadingModal}
                onChange={handleChange}
                onSubmit={submit}
                onClose={closeModal}
            />
        </div>
    );
}
